import { useState, useRef, useEffect } from "react";

const MONTHLY_LIMIT = 100;

// Mock paper context - in real app, pass this as a prop from your analysis state
const DEFAULT_PAPER_CONTEXT = `You are an expert research assistant helping a user understand a scientific paper. 
Answer questions clearly and accurately based on the paper content provided. 
Be concise but thorough. If something isn't covered in the paper, say so honestly.`;

const BotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#2dd4bf" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#2dd4bf" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="#94a3b8" strokeWidth="1.5"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SUGGESTED_QUESTIONS = [
  "What are the key limitations of this study?",
  "Explain the methodology in simple terms",
  "What future research does this suggest?",
  "What's the most surprising finding?",
];

export default function AskThePaper({ paperTitle = "Bañados 2018", paperText = "", questionsUsed = 0, onQuestionAsked }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `I've read **${paperTitle}** in full. Ask me anything about it — methodology, findings, limitations, or how it connects to broader research.`,
      id: Date.now(),
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [used, setUsed] = useState(questionsUsed);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const remaining = MONTHLY_LIMIT - used;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const question = text || input.trim();
    if (!question || loading || remaining <= 0) return;

    setInput("");
    const userMsg = { role: "user", content: question, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setUsed(prev => prev + 1);
    if (onQuestionAsked) onQuestionAsked();

    try {
      const history = messages.map(m => ({
        role: m.role,
        content: m.content.replace(/\*\*/g, "")
      }));

      const systemPrompt = `${DEFAULT_PAPER_CONTEXT}

Paper title: ${paperTitle}
${paperText ? `Paper content:\n${paperText}` : "Note: Full paper text not provided. Answer based on general knowledge about this paper if known, and be transparent about limitations."}`;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5",
          max_tokens: 1000,
          system: systemPrompt,
          messages: [
            ...history,
            { role: "user", content: question }
          ]
        })
      });

      const data = await response.json();
      const reply = data.content?.[0]?.text || "Sorry, I couldn't generate a response.";
      setMessages(prev => [...prev, { role: "assistant", content: reply, id: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Something went wrong. Please try again.",
        id: Date.now()
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatContent = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div style={{
      background: "#0f1923",
      border: "1px solid #1e2d3d",
      borderRadius: "12px",
      display: "flex",
      flexDirection: "column",
      height: "560px",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      overflow: "hidden",
    }}>

      {/* Header */}
      <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid #1e2d3d",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#0d1620",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SparkleIcon />
          <span style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "14px" }}>Ask the paper</span>
          <span style={{
            background: "#14303f",
            color: "#2dd4bf",
            fontSize: "11px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "20px",
            border: "1px solid #1e4a5a",
            letterSpacing: "0.03em"
          }}>PRO</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{
            width: "80px",
            height: "4px",
            background: "#1e2d3d",
            borderRadius: "2px",
            overflow: "hidden"
          }}>
            <div style={{
              width: `${(remaining / MONTHLY_LIMIT) * 100}%`,
              height: "100%",
              background: remaining > 20 ? "#2dd4bf" : remaining > 10 ? "#f59e0b" : "#ef4444",
              borderRadius: "2px",
              transition: "width 0.3s"
            }} />
          </div>
          <span style={{ fontSize: "11px", color: "#64748b" }}>{remaining} left</span>
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        scrollbarWidth: "thin",
        scrollbarColor: "#1e2d3d transparent",
      }}>
        {messages.map((msg) => (
          <div key={msg.id} style={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            flexDirection: msg.role === "user" ? "row-reverse" : "row",
          }}>
            {/* Avatar */}
            <div style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: msg.role === "assistant" ? "#0d2535" : "#1e2d3d",
              border: `1px solid ${msg.role === "assistant" ? "#1e4a5a" : "#2d3f52"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              {msg.role === "assistant" ? <BotIcon /> : <UserIcon />}
            </div>

            {/* Bubble */}
            <div style={{
              maxWidth: "80%",
              background: msg.role === "assistant" ? "#111f2e" : "#0f2d3d",
              border: `1px solid ${msg.role === "assistant" ? "#1e2d3d" : "#1a3f52"}`,
              borderRadius: msg.role === "assistant" ? "4px 12px 12px 12px" : "12px 4px 12px 12px",
              padding: "10px 14px",
              color: "#cbd5e1",
              fontSize: "13.5px",
              lineHeight: "1.65",
            }}
              dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
            />
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "50%",
              background: "#0d2535", border: "1px solid #1e4a5a",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
            }}>
              <BotIcon />
            </div>
            <div style={{
              background: "#111f2e", border: "1px solid #1e2d3d",
              borderRadius: "4px 12px 12px 12px", padding: "12px 16px",
              display: "flex", gap: "4px", alignItems: "center"
            }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: "#2dd4bf", opacity: 0.4,
                  animation: "bounce 1.2s infinite",
                  animationDelay: `${i * 0.2}s`
                }} />
              ))}
            </div>
          </div>
        )}

        {/* Suggested questions — show only at start */}
        {messages.length === 1 && !loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
            <span style={{ fontSize: "11px", color: "#475569", paddingLeft: "38px" }}>Try asking:</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", paddingLeft: "38px" }}>
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)} style={{
                  background: "transparent",
                  border: "1px solid #1e2d3d",
                  borderRadius: "20px",
                  padding: "5px 12px",
                  color: "#94a3b8",
                  fontSize: "12px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  fontFamily: "inherit",
                }}
                  onMouseEnter={e => { e.target.style.borderColor = "#2dd4bf"; e.target.style.color = "#2dd4bf"; }}
                  onMouseLeave={e => { e.target.style.borderColor = "#1e2d3d"; e.target.style.color = "#94a3b8"; }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: "12px 14px",
        borderTop: "1px solid #1e2d3d",
        background: "#0d1620",
      }}>
        {remaining <= 0 ? (
          <div style={{
            textAlign: "center", color: "#64748b", fontSize: "13px", padding: "8px"
          }}>
            Monthly limit reached. Resets on your next billing date.
          </div>
        ) : (
          <div style={{
            display: "flex", gap: "8px", alignItems: "flex-end",
            background: "#111f2e", border: "1px solid #1e2d3d",
            borderRadius: "10px", padding: "8px 10px",
            transition: "border-color 0.15s",
          }}
            onFocus={() => {}}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask anything about this paper..."
              rows={1}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                color: "#e2e8f0", fontSize: "13.5px", lineHeight: "1.5",
                resize: "none", fontFamily: "inherit",
                maxHeight: "100px", overflowY: "auto",
                scrollbarWidth: "none",
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                background: input.trim() && !loading ? "#2dd4bf" : "#1e2d3d",
                border: "none", borderRadius: "7px",
                width: "32px", height: "32px",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: input.trim() && !loading ? "pointer" : "default",
                transition: "background 0.15s",
                color: input.trim() && !loading ? "#0a1a24" : "#475569",
                flexShrink: 0,
              }}
            >
              <SendIcon />
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        textarea::placeholder { color: #475569; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1e2d3d; border-radius: 2px; }
      `}</style>
    </div>
  );
}
